import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import './animations.css';

import L0 from '../../assets/character/chick_L0.png';
import L1 from '../../assets/character/chick_L1.png';
import L2 from '../../assets/character/chick_L2.png';
import L3 from '../../assets/character/chick_L3.png';
import L4 from '../../assets/character/chick_L4.png';
import L5 from '../../assets/character/chick_L5.png';
import L6 from '../../assets/character/chick_L6.png';
import L7 from '../../assets/character/chick_L7.png';
import L8 from '../../assets/character/chick_L8.png';
import L9 from '../../assets/character/chick_L9.png';
import L10 from '../../assets/character/chick_L10.png';
import L11 from '../../assets/character/chick_L11.png';
import L12 from '../../assets/character/chick_L12.png';

const IMAGES = [L0, L1, L2, L3, L4, L5, L6, L7, L8, L9, L10, L11, L12];

/**
 * 레벨에 따른 캐릭터 PNG를 렌더링.
 * - wander=true: 컨테이너 안에서 좌→우→좌 어슬렁거림 (헤더용)
 * - wander=false: 정적 표시 (통계 카드용)
 * - onTap: 탭 핸들러. 탭 시 점프 모션 1회 재생
 * - jumpTrigger: 외부에서 점프(big-jump 또는 wiggle) 트리거. 값 변경 시 재생
 * - evolving: 진화 sparkle 별 표시 (1.3s)
 * - flyAway: 화면 밖으로 날아갔다 돌아옴 (3.5s)
 *
 * 알 단계(L1, L2)는 다리가 없어 점프 대신 좌우 꿈틀(wiggle) 모션.
 */
export default function PetCharacter({
    level = 0,
    size = 36,
    wander = false,
    onTap,
    paused: pausedProp,
    jumpTrigger = 0,
    evolving = false,
    flyAway = false,
}) {
    const [jumping, setJumping] = useState(false);
    const [bigJumping, setBigJumping] = useState(false);
    const [docHidden, setDocHidden] = useState(false);
    const jumpTimerRef = useRef(null);
    const bigJumpTimerRef = useRef(null);
    const bigJumpRestartRef = useRef(null);

    const safeLevel = Math.max(0, Math.min(12, level || 0));
    const isStatic = safeLevel === 0;
    const isEgg = safeLevel >= 1 && safeLevel <= 2; // 알/부화 직전 — wiggle만

    useEffect(() => {
        const onVis = () => setDocHidden(document.hidden);
        document.addEventListener('visibilitychange', onVis);
        return () => document.removeEventListener('visibilitychange', onVis);
    }, []);

    useEffect(() => () => {
        if (jumpTimerRef.current) clearTimeout(jumpTimerRef.current);
        if (bigJumpTimerRef.current) clearTimeout(bigJumpTimerRef.current);
        if (bigJumpRestartRef.current) clearTimeout(bigJumpRestartRef.current);
    }, []);

    // jumpTrigger 바뀌면 점프(또는 wiggle) 재생. 강제 재시작 위해 false → 20ms → true.
    useEffect(() => {
        if (!jumpTrigger) return;
        if (isStatic) return; // L0(둥지)은 모션 없음
        if (bigJumpTimerRef.current) clearTimeout(bigJumpTimerRef.current);
        if (bigJumpRestartRef.current) clearTimeout(bigJumpRestartRef.current);
        setBigJumping(false);
        const duration = isEgg ? 900 : 1050;
        bigJumpRestartRef.current = setTimeout(() => {
            setBigJumping(true);
            bigJumpTimerRef.current = setTimeout(() => setBigJumping(false), duration);
        }, 20);
    }, [jumpTrigger, isEgg, isStatic]);

    const handleTap = (e) => {
        e.preventDefault?.();
        if (jumpTimerRef.current) clearTimeout(jumpTimerRef.current);
        setJumping(true);
        jumpTimerRef.current = setTimeout(() => setJumping(false), 600);
        if (onTap) onTap(e);
    };

    const paused = pausedProp ?? docHidden;
    const imgSrc = IMAGES[safeLevel];

    // 액션별 우선순위: flyAway > bigJump/wiggle > tap-jump > bob
    const activeMotion =
        flyAway && !isEgg ? 'pet-fly-away' :
        flyAway && isEgg ? 'pet-wiggle' :
        bigJumping ? (isEgg ? 'pet-wiggle' : 'pet-big-jump') :
        jumping ? 'pet-tap-jump' :
        (!isStatic ? 'pet-bob' : '');

    const imgInner = (
        <Box
            className={activeMotion}
            sx={{
                width: '100%', height: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
        >
            <img
                src={imgSrc}
                alt=""
                draggable={false}
                style={{
                    width: '100%', height: '100%',
                    objectFit: 'contain',
                    pointerEvents: 'none',
                    userSelect: 'none',
                }}
            />
        </Box>
    );

    const sparkles = evolving ? (
        <>
            <span className="evolve-sparkle evolve-sparkle-tl">✨</span>
            <span className="evolve-sparkle evolve-sparkle-tr">⭐</span>
            <span className="evolve-sparkle evolve-sparkle-b">✨</span>
        </>
    ) : null;

    if (wander && !isStatic) {
        return (
            <Box
                role={onTap ? 'button' : undefined}
                tabIndex={onTap ? 0 : undefined}
                onClick={onTap ? handleTap : undefined}
                className={['pet-wander-container', paused ? 'pet-paused' : ''].join(' ')}
                style={{ '--pet-size': `${size}px` }}
                sx={{
                    cursor: onTap ? 'pointer' : 'default',
                    '&:active': { opacity: 0.7 },
                }}
            >
                {imgInner}
                {sparkles}
            </Box>
        );
    }

    return (
        <Box
            role={onTap ? 'button' : undefined}
            tabIndex={onTap ? 0 : undefined}
            onClick={onTap ? handleTap : undefined}
            sx={{
                position: 'relative',
                width: size, height: size,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: onTap ? 'pointer' : 'inherit',
                overflow: 'visible',
                '&:active': { opacity: onTap ? 0.7 : 1 },
            }}
        >
            {imgInner}
            {sparkles}
        </Box>
    );
}
